import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, Plus, Save, Trash2, Loader2, Eye, EyeOff,
  Crown, GripVertical, Check, X, Palette
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { aurasApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

// Aura Preview Component
const AuraPreview = ({ colors, size = 'md' }) => {
  if (!colors || colors.length === 0) {
    return (
      <div className={`${size === 'lg' ? 'w-32 h-32' : 'w-16 h-16'} rounded-full bg-[#27272A] flex items-center justify-center`}>
        <EyeOff className="text-[#52525B]" size={size === 'lg' ? 32 : 16} />
      </div>
    );
  }

  const sizeClasses = size === 'lg' ? 'w-32 h-32' : 'w-16 h-16';
  
  return (
    <div className={`${sizeClasses} relative`}>
      {/* Animated aura rings */}
      {colors.slice(0, 3).map((color, i) => (
        <motion.div
          key={i}
          className="absolute inset-0 rounded-full"
          style={{
            background: `radial-gradient(circle, ${color}40 0%, transparent 70%)`,
            transform: `scale(${1 + i * 0.3})`,
          }}
          animate={{
            scale: [1 + i * 0.3, 1.2 + i * 0.3, 1 + i * 0.3],
            opacity: [0.6, 0.3, 0.6],
          }}
          transition={{
            duration: 2 + i * 0.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
      {/* Center circle */}
      <div 
        className="absolute inset-2 rounded-full"
        style={{
          background: `conic-gradient(from 0deg, ${colors.join(', ')})`,
          filter: 'blur(4px)',
        }}
      />
      <motion.div 
        className="absolute inset-3 rounded-full bg-[#121212]"
        animate={{ rotate: 360 }}
        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
      />
    </div>
  );
};

// Color Picker Input
const ColorInput = ({ value, onChange, label }) => (
  <div className="space-y-1">
    <Label className="text-xs text-[#A1A1AA]">{label}</Label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded cursor-pointer border-0 bg-transparent"
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-[#0A0A0A] border-[#27272A] font-mono text-sm flex-1"
        placeholder="#FFFFFF"
      />
    </div>
  </div>
);

const AurasAdminPage = () => {
  const { hasPermission } = useAuth();
  const [auras, setAuras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingAura, setEditingAura] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    colors: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'],
    premium: false,
    is_active: true,
  });

  useEffect(() => {
    loadAuras();
  }, []);

  const loadAuras = async () => {
    try {
      const response = await aurasApi.getPresets(true);
      setAuras(response.data);
    } catch (error) {
      console.error('Error loading auras:', error);
      toast.error('Error al cargar auras');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingAura(null);
    setFormData({
      name: '',
      colors: ['#FFFFFF', '#E0E0E0', '#BDBDBD', '#9E9E9E', '#FFFFFF', '#F5F5F5'],
      premium: false,
      is_active: true,
    });
    setShowModal(true);
  };

  const openEditModal = (aura) => {
    setEditingAura(aura);
    setFormData({
      name: aura.name,
      colors: aura.colors?.length === 6 ? [...aura.colors] : ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'],
      premium: aura.premium,
      is_active: aura.is_active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Ingresa un nombre');
      return;
    }

    setSaving(true);
    try {
      if (editingAura) {
        await aurasApi.updatePreset(editingAura.id, formData);
        toast.success('Aura actualizada');
      } else {
        await aurasApi.createPreset(formData);
        toast.success('Aura creada');
      }
      setShowModal(false);
      loadAuras();
    } catch (error) {
      console.error('Error saving aura:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (aura) => {
    if (aura.id === 'monochrome' || aura.id === 'off') {
      toast.error('No se pueden eliminar las auras predeterminadas');
      return;
    }
    
    if (!confirm(`¿Eliminar aura "${aura.name}"?`)) return;
    
    try {
      await aurasApi.deletePreset(aura.id);
      toast.success('Aura eliminada');
      loadAuras();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const toggleActive = async (aura) => {
    try {
      await aurasApi.updatePreset(aura.id, { is_active: !aura.is_active });
      loadAuras();
    } catch (error) {
      console.error('Error toggling:', error);
      toast.error('Error al actualizar');
    }
  };

  const togglePremium = async (aura) => {
    if (aura.id === 'monochrome' || aura.id === 'off') {
      toast.error('No se puede cambiar el estado premium de las auras predeterminadas');
      return;
    }
    
    try {
      await aurasApi.updatePreset(aura.id, { premium: !aura.premium });
      loadAuras();
    } catch (error) {
      console.error('Error toggling premium:', error);
      toast.error('Error al actualizar');
    }
  };

  const updateColor = (index, value) => {
    const newColors = [...formData.colors];
    newColors[index] = value;
    setFormData({ ...formData, colors: newColors });
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="p-6 lg:p-8 flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-6 lg:p-8 space-y-6"
        >
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                <Sparkles className="text-[#D4AF37]" size={28} />
                Gestión de Auras
              </h1>
              <p className="text-zinc-500 text-sm mt-1">Personaliza las auras disponibles para los usuarios</p>
            </div>
            <Button onClick={openCreateModal} className="admin-btn-primary">
              <Plus className="mr-2" size={16} />
              Nueva Aura
            </Button>
          </div>

          {/* Auras Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {auras.map((aura) => (
              <motion.div
                key={aura.id}
                className={`
                  admin-card rounded-xl p-6 relative overflow-hidden
                  ${aura.is_active ? 'border-[#27272A]' : 'border-red-500/30 opacity-60'}
                `}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                {/* Premium Badge */}
                {aura.premium && (
                  <div className="absolute top-3 right-3">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-yellow-500/20 text-yellow-500 text-xs font-medium">
                      <Crown size={12} />
                      Premium
                    </span>
                  </div>
                )}

                {/* Preview */}
                <div className="flex justify-center mb-4">
                  <AuraPreview colors={aura.colors} size="lg" />
                </div>

                {/* Info */}
                <div className="text-center mb-4">
                  <h3 className="font-bold text-lg text-white">{aura.name}</h3>
                </div>

                {/* Color Preview Strip */}
                {aura.colors && aura.colors.length > 0 && (
                  <div className="flex gap-1 mb-4 justify-center">
                    {aura.colors.map((color, i) => (
                      <div
                        key={i}
                        className="w-6 h-6 rounded-full border border-[#27272A]"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-[#27272A]">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleActive(aura)}
                      className={`p-2 rounded-lg transition-colors ${
                        aura.is_active 
                          ? 'bg-green-500/20 text-green-500' 
                          : 'bg-red-500/20 text-red-500'
                      }`}
                      title={aura.is_active ? 'Desactivar' : 'Activar'}
                    >
                      {aura.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                    <button
                      onClick={() => togglePremium(aura)}
                      className={`p-2 rounded-lg transition-colors ${
                        aura.premium 
                          ? 'bg-yellow-500/20 text-yellow-500' 
                          : 'bg-[#27272A] text-[#52525B]'
                      }`}
                      title={aura.premium ? 'Quitar Premium' : 'Hacer Premium'}
                    >
                      <Crown size={16} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditModal(aura)}
                      className="text-[#A1A1AA] hover:text-white"
                    >
                      <Palette size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(aura)}
                      className="text-[#A1A1AA] hover:text-red-500"
                      disabled={aura.id === 'monochrome' || aura.id === 'off'}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="text-purple-500" size={20} />
              {editingAura ? 'Editar Aura' : 'Nueva Aura'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre del aura"
                  className="bg-[#0A0A0A] border-[#27272A]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                {formData.colors.map((color, index) => (
                  <ColorInput
                    key={index}
                    value={color}
                    onChange={(value) => updateColor(index, value)}
                    label={`Color ${index + 1}`}
                  />
                ))}
              </div>

              <div className="flex items-center justify-between pt-4">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={formData.premium}
                    onCheckedChange={(checked) => setFormData({ ...formData, premium: checked })}
                  />
                  <Label className="flex items-center gap-2">
                    <Crown size={14} className="text-yellow-500" />
                    Premium
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label>Activo</Label>
                </div>
              </div>
            </div>

            {/* Right: Preview */}
            <div className="bg-[#0A0A0A] rounded-xl p-6 flex flex-col items-center justify-center">
              <p className="text-[#52525B] text-xs uppercase tracking-wider mb-4">Vista previa</p>
              <AuraPreview colors={formData.colors} size="lg" />
              <p className="text-white font-medium mt-4">{formData.name || 'Sin nombre'}</p>
              {formData.premium && (
                <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 rounded bg-yellow-500/20 text-yellow-500 text-xs">
                  <Crown size={12} />
                  Premium
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-[#27272A]">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="admin-btn-primary"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save className="mr-2" size={16} />}
              {editingAura ? 'Guardar cambios' : 'Crear aura'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
        </motion.div>
        </AdminLayout>
      </SidebarProvider>
  );
};

export default AurasAdminPage;
